import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    addVariantToGroup,
    createNewGroup,
    deleteGroup,
    deleteVariantFromGroup,
    getGroupsData,
    ProductGroupLoadingKeys,
    updateGroupName,
} from "../../slices/productGroupSlice";
import { fetchProductCatalogCatalogSkus } from "../../slices/productCatalogSlice";
import Drawer from "../Drawer";
import { LoaderSpinner } from "../LoaderSpinner";
import { ConfrimModal } from "../ConfrimModal";
import { useDebounce } from "../../helpers/useDebounce";

/**
 * Products & Variants drawer: groups from `GET /api/v1/group-data`, create group, search, add/remove variants.
 * @param {boolean} [embedded] When true, render body + modals only (parent provides shell).
 */
export const ProductCatalogProductsVariantsSidebar = ({ isOpen, onClose, refetchCatalog, embedded = false }) => {
    const dispatch = useDispatch();
    const { groups, loadingKeys } = useSelector((s) => s.productGroup);

    const [expandedGroupIds, setExpandedGroupIds] = useState({});
    const [newGroupName, setNewGroupName] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearch = useDebounce(searchInput, 400);

    const [addSkuGroupId, setAddSkuGroupId] = useState(null);
    const [addSkuSearch, setAddSkuSearch] = useState("");
    const debouncedAddSkuSearch = useDebounce(addSkuSearch, 300);
    const [addSkuOptions, setAddSkuOptions] = useState([]);
    const [addSkuLoading, setAddSkuLoading] = useState(false);

    const [deleteVariantModalOpened, setDeleteVariantModalOpened] = useState(false);
    const [deleteGroupModalOpened, setDeleteGroupModalOpened] = useState(false);
    const [variantIdToDelete, setVariantIdToDelete] = useState("");
    const [groupIdToDelete, setGroupIdToDelete] = useState("");
    const [groupIdForVariantDelete, setGroupIdForVariantDelete] = useState("");
    const [vendorIdToDelete, setVendorIdToDelete] = useState(null);

    /** Inline rename product group (`ProductGroup.id`; API `/api/v1/update-group-name` uses param name `productId`). */
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupNameDraft, setEditGroupNameDraft] = useState("");

    const loadingGroups =
        loadingKeys.includes(ProductGroupLoadingKeys.getGroupData) ||
        loadingKeys.includes(ProductGroupLoadingKeys.createGroup);
    const loadingDelete =
        loadingKeys.includes(ProductGroupLoadingKeys.deleteGroup) ||
        loadingKeys.includes(ProductGroupLoadingKeys.deleteVariant);
    const deleteVariantIsLoading = () =>
        loadingKeys.includes(ProductGroupLoadingKeys.deleteVariant);
    const deleteGroupIsLoading = () =>
        loadingKeys.includes(ProductGroupLoadingKeys.deleteGroup);
    const loadingAddVariant = loadingKeys.includes(ProductGroupLoadingKeys.addVariantToGroup);
    const loadingRenameGroup = loadingKeys.includes(ProductGroupLoadingKeys.updateName);

    const refetchGroups = useCallback(() => {
        dispatch(
            getGroupsData({
                search: debouncedSearch,
                page: 1,
                itemsPerPage: 200,
            })
        );
    }, [dispatch, debouncedSearch]);

    const syncCatalogTable = useCallback(() => {
        refetchCatalog?.();
    }, [refetchCatalog]);

    useEffect(() => {
        if (!isOpen) return;
        refetchGroups();
    }, [isOpen, debouncedSearch, refetchGroups]);

    useEffect(() => {
        if (!isOpen) {
            setEditingGroupId(null);
            setEditGroupNameDraft("");
        }
    }, [isOpen]);

    const toggleGroupExpanded = (groupId) => {
        setExpandedGroupIds((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    const handleCreateGroup = async () => {
        const name = newGroupName.trim();
        if (!name) return;
        try {
            await dispatch(
                createNewGroup({
                    creationData: {
                        name,
                        lead_time: 40,
                        category_group_id: null,
                        vendor_id: null,
                    },
                })
            ).unwrap();
            setNewGroupName("");
            refetchGroups();
        } catch {
            /* toast handled in slice */
        }
    };

    const openDeleteGroupModal = (groupId) => {
        setGroupIdToDelete(groupId);
        setDeleteGroupModalOpened(true);
    };

    const startEditingGroupName = (group) => {
        const id = group?.id;
        if (id == null) return;
        setEditingGroupId(id);
        setEditGroupNameDraft(String(group?.name ?? ""));
        if (addSkuGroupId === id) {
            setAddSkuGroupId(null);
            setAddSkuSearch("");
            setAddSkuOptions([]);
        }
    };

    const cancelEditingGroupName = () => {
        setEditingGroupId(null);
        setEditGroupNameDraft("");
    };

    const submitEditingGroupName = async (groupId) => {
        const name = String(editGroupNameDraft ?? "").trim();
        if (!name || groupId == null) return;
        try {
            await dispatch(
                updateGroupName({
                    productId: groupId,
                    name,
                })
            ).unwrap();
            cancelEditingGroupName();
            refetchGroups();
            syncCatalogTable();
        } catch {
            /* toast in slice */
        }
    };

    const openRemoveVariantModal = (groupId, variant) => {
        setVariantIdToDelete(variant.id);
        setGroupIdForVariantDelete(groupId);
        setVendorIdToDelete(variant.vendor_id ?? null);
        setDeleteVariantModalOpened(true);
    };

    useEffect(() => {
        if (!addSkuGroupId || !isOpen) {
            setAddSkuOptions([]);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setAddSkuLoading(true);
            try {
                const data = await dispatch(
                    fetchProductCatalogCatalogSkus({
                        search: debouncedAddSkuSearch.trim(),
                        excludeProductId: "",
                        limit: 50,
                        excludeByProductAndVariantsTab: true
                    })
                ).unwrap();
                if (!cancelled) {
                    setAddSkuOptions(data?.items || []);
                }
            } catch {
                if (!cancelled) setAddSkuOptions([]);
            } finally {
                if (!cancelled) setAddSkuLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [dispatch, addSkuGroupId, debouncedAddSkuSearch, isOpen]);

    const handlePickVariantForGroup = async (groupId, product) => {
        if (!product?.id) return;
        try {
            await dispatch(
                addVariantToGroup({
                    groupId,
                    variantId: product.id,
                    vendorId: null,
                })
            ).unwrap();
            setAddSkuGroupId(null);
            setAddSkuSearch("");
            setAddSkuOptions([]);
            refetchGroups();
            syncCatalogTable();
        } catch {
            /* toast may be added in slice */
        }
    };

    const deleteModals = (
        <>
            <ConfrimModal
                isOpen={deleteVariantModalOpened}
                title={"Are you sure you want to remove this variant from the group?"}
                confirmButtonTitle={"Remove"}
                isLoading={deleteVariantIsLoading()}
                containerClassName={"!max-w-[580px]"}
                submitClick={async () => {
                    setDeleteVariantModalOpened(false);
                    try {
                        await dispatch(
                            deleteVariantFromGroup({
                                productId: variantIdToDelete,
                                groupId: groupIdForVariantDelete,
                                vendorId: vendorIdToDelete,
                                deleteManually: true,
                            })
                        ).unwrap();
                        refetchGroups();
                        syncCatalogTable();
                    } catch {
                        /* toast may be added in slice */
                    }
                    setVariantIdToDelete("");
                    setGroupIdForVariantDelete("");
                    setVendorIdToDelete(null);
                }}
                cancelClick={() => {
                    setDeleteVariantModalOpened(false);
                    setVariantIdToDelete("");
                    setGroupIdForVariantDelete("");
                    setVendorIdToDelete(null);
                }}
            />
            <ConfrimModal
                isOpen={deleteGroupModalOpened}
                title={
                    "Are you sure you want to delete this product? This will remove the product and disconnect all connected variants."
                }
                confirmButtonTitle={"Delete Group"}
                isLoading={deleteGroupIsLoading()}
                containerClassName={"!max-w-[580px]"}
                submitClick={async () => {
                    setDeleteGroupModalOpened(false);
                    try {
                        await dispatch(
                            deleteGroup({
                                groupId: groupIdToDelete,
                                deleteManually: true,
                            })
                        ).unwrap();
                        refetchGroups();
                        syncCatalogTable();
                    } catch {
                        /* toast may be added in slice */
                    }
                    setGroupIdToDelete("");
                }}
                cancelClick={() => {
                    setDeleteGroupModalOpened(false);
                    setGroupIdToDelete("");
                }}
            />
        </>
    );

    const innerContent = (
        <div className="space-y-2 px-3 py-3">
            <div className="flex gap-1.5">
                <input
                    type="text"
                    placeholder="New product name..."
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            void handleCreateGroup();
                        }
                    }}
                    className="min-w-0 flex-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                    type="button"
                    onClick={() => void handleCreateGroup()}
                    disabled={!newGroupName.trim() || loadingKeys.includes(ProductGroupLoadingKeys.createGroup)}
                    className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
                    title="Create group"
                >
                    <PlusIcon className="h-5 w-5" />
                </button>
            </div>

            <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search products..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                />
            </div>

            <div className={`relative ${loadingGroups && !groups?.length ? "h-[calc(100vh-200px)]" : "max-h-[calc(100vh-200px)] overflow-y-auto"} rounded-md border border-gray-200 bg-gray-50/40`}>
                {loadingGroups && !groups?.length ? (
                    <div className="flex justify-center py-6">
                        <LoaderSpinner width="40" height="40" border="3" colorSpinner="black" detached />
                    </div>
                ) : !groups?.length ? (
                    <p className="px-3 py-4 text-center text-xs text-gray-500">No groups found.</p>
                ) : (
                    <div className="flex flex-col gap-3 p-3">
                        {groups.map((group) => {
                            const gid = group.id;
                            const expanded = Boolean(expandedGroupIds[gid]);
                            const variants = group.variants || [];
                            const count = variants.length;
                            return (
                                <section
                                    key={gid}
                                    className="rounded-md border border-gray-200 bg-white overflow-hidden"
                                >
                                    <div className="flex items-stretch">
                                        {editingGroupId === gid ? (
                                            <>
                                                <div className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2">
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        value={editGroupNameDraft}
                                                        onChange={(e) => setEditGroupNameDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                void submitEditingGroupName(gid);
                                                            }
                                                            if (e.key === "Escape") {
                                                                e.preventDefault();
                                                                cancelEditingGroupName();
                                                            }
                                                        }}
                                                        disabled={loadingRenameGroup}
                                                        className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50"
                                                        aria-label="Group name"
                                                    />
                                                    <button
                                                        type="button"
                                                        title="Save name"
                                                        disabled={
                                                            loadingRenameGroup ||
                                                            !String(editGroupNameDraft ?? "").trim()
                                                        }
                                                        onClick={() => void submitEditingGroupName(gid)}
                                                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                                                    >
                                                        <CheckIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Cancel"
                                                        disabled={loadingRenameGroup}
                                                        onClick={cancelEditingGroupName}
                                                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                                    >
                                                        <XMarkIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => openDeleteGroupModal(gid)}
                                                    className="shrink-0 border-l border-gray-200 px-3 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                                    title="Delete group"
                                                >
                                                    <XMarkIcon className="h-4 w-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleGroupExpanded(gid)}
                                                    className="flex flex-1 min-w-0 items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-all duration-200"
                                                    aria-expanded={expanded}
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {group.name}
                                                        </p>
                                                        <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500 shrink-0 tabular-nums">
                                                            {count}
                                                        </span>
                                                    </div>
                                                    {expanded ? (
                                                        <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                                    ) : (
                                                        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => startEditingGroupName(group)}
                                                    className="shrink-0 border-l border-gray-200 px-3 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                                    title="Rename group"
                                                >
                                                    <PencilSquareIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openDeleteGroupModal(gid)}
                                                    className="shrink-0 border-l border-gray-200 px-3 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                                                    title="Delete group"
                                                >
                                                    <XMarkIcon className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {expanded && (
                                        <div className="border-t border-gray-200 px-4 py-4 flex flex-col gap-3 bg-gray-50/40">
                                            {count === 0 ? (
                                                <p className="text-xs italic text-gray-400">
                                                    No SKUs assigned. Use the button below to add from catalog.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {variants.map((v) => (
                                                        <div
                                                            key={v.id}
                                                            className="inline-flex max-w-full items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
                                                        >
                                                            <span className="truncate" title={v.sku}>
                                                                {v.sku || v.id}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => openRemoveVariantModal(gid, v)}
                                                                className="shrink-0 text-gray-400 hover:text-gray-600"
                                                                title="Remove from group"
                                                            >
                                                                <XMarkIcon className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {addSkuGroupId === gid ? (
                                                <div className="space-y-2 border-t border-gray-200 pt-3 flex flex-col gap-2">
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        placeholder="Search SKU..."
                                                        value={addSkuSearch}
                                                        onChange={(e) => setAddSkuSearch(e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                    {addSkuLoading && (
                                                        <p className="text-xs text-gray-400">Searching…</p>
                                                    )}
                                                    <ul className="max-h-28 overflow-y-auto rounded-md border border-gray-200 bg-white">
                                                        {(addSkuOptions || []).map((p) => (
                                                            <li key={p.id}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        void handlePickVariantForGroup(gid, p)
                                                                    }
                                                                    disabled={loadingAddVariant}
                                                                    className="w-full truncate px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
                                                                >
                                                                    <span className="font-medium">{p.sku}</span>
                                                                    {p.name ? (
                                                                        <span className="text-gray-500">
                                                                            {" "}
                                                                            — {p.name}
                                                                        </span>
                                                                    ) : null}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setAddSkuGroupId(null);
                                                            setAddSkuSearch("");
                                                        }}
                                                        className="text-xs text-gray-500 hover:text-gray-800"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setAddSkuGroupId(gid);
                                                        setAddSkuSearch("");
                                                    }}
                                                    className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <PlusIcon className="h-4 w-4" />
                                                    Add SKU
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                )}
                {(loadingDelete || loadingAddVariant) && groups?.length ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/40">
                        <LoaderSpinner width="32" height="32" border="3" colorSpinner="black" detached />
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (embedded) {
        return isOpen ? (
            <>
                {innerContent}
                {deleteModals}
            </>
        ) : null;
    }

    return (
        <Drawer isOpen={isOpen} onClose={onClose} title="Products & Variants" width="500px">
            {isOpen ? (
                <>
                    {innerContent}
                    {deleteModals}
                </>
            ) : null}
        </Drawer>
    );
};
